import { Link } from '@tanstack/react-router'
import { useState, useRef, useEffect } from 'react'
import type { ReactNode } from 'react'
import { useAuth, useIsAdmin } from '../auth'
import { BrandLockup } from './BrandLockup'
import styles from './Layout.module.css'

interface LayoutProps {
  children: ReactNode
}

export function Layout({ children }: LayoutProps) {
  const { isAuthenticated, user, login, logout } = useAuth()
  const isAdmin = useIsAdmin()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className={styles.layout}>
      <header className={styles.header}>
        <nav className={styles.nav}>
          <Link to="/" className={styles.logo} aria-label="Tasamago home">
            <BrandLockup variant="nav" />
          </Link>
          <div className={styles.links}>
            <Link to="/" className={styles.navLink}>HOME</Link>
            <Link to="/order/new" className={styles.navLink}>ORDER</Link>
            <Link to="/orders" className={styles.navLink}>MY ORDERS</Link>
          </div>
          <div className={styles.userSection} ref={menuRef}>
            {isAuthenticated ? (
              <>
                <button 
                  className={styles.userButton}
                  onClick={() => setMenuOpen(!menuOpen)}
                >
                  {user?.name || 'USER'}
                </button>
                {menuOpen && (
                  <div className={styles.userMenu}>
                    <div className={styles.userMenuHeader}>
                      <span className={styles.userName}>{user?.name}</span>
                      <span className={styles.userEmail}>{user?.email}</span>
                    </div>
                    <div className={styles.userMenuDivider} />
                    {isAdmin && (
                      <Link
                        to="/admin"
                        className={styles.menuItemAccent}
                        onClick={() => setMenuOpen(false)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="7" height="7"/>
                          <rect x="14" y="3" width="7" height="7"/>
                          <rect x="14" y="14" width="7" height="7"/>
                          <rect x="3" y="14" width="7" height="7"/>
                        </svg>
                        DASHBOARD
                      </Link>
                    )}
                    <Link
                      to="/addresses"
                      className={styles.menuItem}
                      onClick={() => setMenuOpen(false)}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                        <circle cx="12" cy="10" r="3"/>
                      </svg>
                      DELIVERY ADDRESSES
                    </Link>
                    <Link
                      to="/account"
                      className={styles.menuItem}
                      onClick={() => setMenuOpen(false)}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="3"/>
                        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/>
                      </svg>
                      ACCOUNT SETTINGS
                    </Link>
                    <div className={styles.userMenuDivider} />
                    <button 
                      className={styles.menuItemDanger}
                      onClick={() => {
                        setMenuOpen(false)
                        logout()
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                        <polyline points="16 17 21 12 16 7"/>
                        <line x1="21" y1="12" x2="9" y2="12"/>
                      </svg>
                      LOGOUT
                    </button>
                  </div>
                )}
              </>
            ) : (
              <button className={styles.loginButton} onClick={login}>
                LOGIN
              </button>
            )}
          </div>
        </nav>
      </header>
      <main className={styles.main}>
        {children}
      </main>
    </div>
  )
}
